import {
  ScheduleEntryState,
  Task,
  TaskCheckOptions,
  TaskType,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { isAfter, isBefore, isSameDay } from 'date-fns';

import { getScheduleDayTarget } from './get-schedule-day-target';
import { getDueTaskPlanning } from './get-task-planning';
import { getScheduleByDate } from './schedules';

export type GetTaskCheckOptionOptions = {
  day: Timestamp;
  today: Timestamp;
  showRepeatingAsTodo?: boolean;
  weekStartsOn: WeekDays;
};

export const getTaskCheckOption = (
  task: Task,
  { day, today, showRepeatingAsTodo, weekStartsOn }: GetTaskCheckOptionOptions,
) => {
  if (task.type === TaskType.Task) {
    return task.completedAt
      ? TaskCheckOptions.Checked
      : TaskCheckOptions.Unchecked;
  }

  const scheduleForDate = getScheduleByDate(task.schedules, {
    date: day,
    today,
  });

  if (task.completedAt || !scheduleForDate) {
    return TaskCheckOptions.RepeatChecked;
  }

  if (!isSameDay(day, today) && isAfter(day, today)) {
    return TaskCheckOptions.RepeatChecked;
  }

  const dateEntry = scheduleForDate.entries.find(({ date }) =>
    isSameDay(date, day),
  );

  if (dateEntry?.state === ScheduleEntryState.Failure) {
    return TaskCheckOptions.FailedChecked;
  }

  if (dateEntry?.state === ScheduleEntryState.Skip) {
    return TaskCheckOptions.SkippedChecked;
  }

  if (scheduleForDate.frequency) {
    if (
      dateEntry?.state === ScheduleEntryState.Complete &&
      (dateEntry?.value ?? 0) >= getScheduleDayTarget(scheduleForDate)
    ) {
      return showRepeatingAsTodo
        ? TaskCheckOptions.Checked
        : TaskCheckOptions.RepeatChecked;
    }

    return TaskCheckOptions.Unchecked;
  }

  if (
    scheduleForDate.entries.find(
      ({ state, date, value }) =>
        isSameDay(date, day) &&
        state === ScheduleEntryState.Complete &&
        (value ?? 0) >= scheduleForDate.completionTarget.count,
    )
  ) {
    return showRepeatingAsTodo
      ? TaskCheckOptions.Checked
      : TaskCheckOptions.RepeatSingleChecked;
  }

  if (isBefore(day, today) && !isSameDay(day, today)) {
    return TaskCheckOptions.Unchecked;
  }

  const planning = getDueTaskPlanning(task, {
    date: day,
    today,
    weekStartsOn,
    uncompletedOnly: true,
  });
  const dueDate = planning[0]?.date;

  if (!dueDate || (!isSameDay(dueDate, day) && isAfter(dueDate, day))) {
    return TaskCheckOptions.RepeatSingleChecked;
  }

  return TaskCheckOptions.Unchecked;
};
